import { useState, useEffect } from 'react';
import { LocalVideoTrack, RemoteVideoTrack } from 'twilio-video';
import * as Sentry from '@sentry/react';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

type TrackType = RemoteVideoTrack | LocalVideoTrack | undefined | null;

// The 'switchedOff' event is emitted when there is not enough bandwidth to support
// a track. See: https://www.twilio.com/docs/video/tutorials/using-bandwidth-profile-api#understanding-track-switch-offs

export default function useIsTrackSwitchedOff(track: TrackType) {
  const [isSwitchedOff, setIsSwitchedOff] = useState(track && track.isSwitchedOff);
  const { room } = useVideoContext();
  useEffect(() => {
    // Reset the value if the 'track' variable changes
    setIsSwitchedOff(track && track.isSwitchedOff);

    if (track) {
      const handleSwitchedOff = () => {
        setIsSwitchedOff(true);
        Sentry.addBreadcrumb({
          type: 'debug',
          category: '###RoomSwitchedOff',
          level: Sentry.Severity.Info,
          data: { ...room, track: track.name } || undefined,
        });
      };
      const handleSwitchedOn = () => {
        setIsSwitchedOff(false);
        Sentry.addBreadcrumb({
          type: 'debug',
          category: '###RoomSwitchedOn',
          level: Sentry.Severity.Info,
          data: { ...room, track: track.name } || undefined,
        });
      };

      track.on('switchedOff', handleSwitchedOff);
      track.on('switchedOn', handleSwitchedOn);
      return () => {
        track.off('switchedOff', handleSwitchedOff);
        track.off('switchedOn', handleSwitchedOn);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  return !!isSwitchedOff;
}
